/**
 * Shared formatting and date helpers — source of truth for the app's currency /
 * percent / date formatting and the period→start-date math.
 */
import { periodCutoffDate } from './chartAxis';

export function formatDate(
  d: Date | string | null | undefined,
  fallback: string = '',
): string {
  if (!d) return fallback;
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function plClass(val: number): 'positive' | 'negative' {
  return val >= 0 ? 'positive' : 'negative';
}

export function toISODate(d?: Date): string {
  return (d || new Date()).toISOString().slice(0, 10);
}

export type PeriodKey = '1M' | '3M' | '6M' | 'YTD' | '1Y' | '2Y' | '5Y' | 'All' | 'Custom';

/** The viewer's own calendar date, `YYYY-MM-DD` — never the UTC one. */
export function localISODate(d: Date = new Date()): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(d.getFullYear(), 4)}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The first date a period's window holds, counted back from the viewer's
 * calendar day by the chart's own rule (`chartAxis.periodCutoffDate`): the day
 * of month clamped to the target month, so 31 March less 1M is 28 February,
 * not 3 March, and the date never shifts by the viewer's UTC offset (audit
 * FINANCE-18). 'All' and 'Max' hold everything; a period this app does not
 * offer is an error, never quietly five years.
 */
export function periodToStartDate(period: PeriodKey | string, today: Date = new Date()): string {
  if (period === 'All' || period === 'Max') return '0000-01-01';
  const cutoff = periodCutoffDate(localISODate(today), period);
  if (cutoff == null) throw new RangeError(`periodToStartDate: unknown period ${JSON.stringify(period)}`);
  return cutoff;
}

/** True when `x` is a finite number strictly greater than zero. */
export function isPositiveFinite(x: number): boolean {
  return Number.isFinite(x) && x > 0;
}

export const Utils = {
  formatDate,
  plClass,
  toISODate, periodToStartDate,
};
